import type { BookCoverArt } from "../../data/books";
import type { Model } from "../../data/models";
import { Character } from "../Character";

type MotifProps = { c: BookCoverArt; model?: Model | undefined };

export function LunarMotif({ c, model }: MotifProps) {
  return (
    <g>
      <line x1="40" y1="30" x2="40" y2="65" stroke={c.accent} strokeWidth="1" opacity="0.6" />
      <ellipse cx="40" cy="75" rx="11" ry="14" fill={c.accent} />
      <rect x="34" y="89" width="12" height="3" fill={c.accent} opacity="0.7" />

      <line x1="260" y1="30" x2="260" y2="58" stroke={c.accent} strokeWidth="1" opacity="0.6" />
      <ellipse cx="260" cy="68" rx="9" ry="12" fill={c.accent} />
      <rect x="255" y="80" width="10" height="2.5" fill={c.accent} opacity="0.7" />

      <line x1="150" y1="30" x2="150" y2="50" stroke={c.accent} strokeWidth="1" opacity="0.6" />
      <ellipse cx="150" cy="60" rx="8" ry="10" fill={c.accent} opacity="0.85" />

      <rect x="137" y="150" width="26" height="30" fill={c.accent} rx="2" />
      <path d="M 137 150 L 150 161 L 163 150" stroke={c.bg} strokeWidth="1" fill="none" />
      <text x="150" y="172" textAnchor="middle" fill={c.bg} fontFamily="Fraunces, Georgia, serif" fontSize="12">
        福
      </text>

      <Character x={150} y={120} model={model} scale={1.8} />
    </g>
  );
}

export function HanukkahMotif({ c, model }: MotifProps) {
  return (
    <g>
      <g transform="translate(150, 75)">
        <rect x="-1.5" y="-8" width="3" height="42" fill={c.accent} />
        <rect x="-26" y="32" width="52" height="4" fill={c.accent} />
        {[-26, -17, -8.5, 0, 8.5, 17, 26].map((px, i) => (
          <g key={i}>
            <rect x={px - 0.8} y="-8" width="1.6" height="40" fill={c.accent} opacity="0.85" />
            <ellipse cx={px} cy="-11" rx="1.5" ry="3" fill="#ffe0a0" />
            <circle cx={px} cy="-14" r="1.1" fill="#fff5d0" opacity="0.8" />
          </g>
        ))}
      </g>
      <g transform="translate(50, 60)" opacity="0.6">
        <polygon points="0,-7 6,3.5 -6,3.5" fill="none" stroke={c.accent} strokeWidth="1" />
        <polygon points="0,7 6,-3.5 -6,-3.5" fill="none" stroke={c.accent} strokeWidth="1" />
      </g>
      <g transform="translate(250, 50)" opacity="0.5">
        <polygon points="0,-6 5,3 -5,3" fill="none" stroke={c.accent} strokeWidth="0.9" />
        <polygon points="0,6 5,-3 -5,-3" fill="none" stroke={c.accent} strokeWidth="0.9" />
      </g>

      <g transform="translate(50, 165)">
        <path d="M -10 -8 L 10 -8 L 10 6 L 0 16 L -10 6 Z" fill={c.accent} />
        <rect x="-2" y="-14" width="4" height="6" fill={c.accent} />
        <text x="0" y="3" textAnchor="middle" fill={c.bg} fontFamily="Fraunces, Georgia, serif" fontSize="10">
          נ
        </text>
      </g>

      <Character x={180} y={160} model={model} scale={1.6} />
    </g>
  );
}

export function DiwaliMotif({ c, model }: MotifProps) {
  return (
    <g>
      {[55, 105, 195, 245].map((cx, i) => {
        const cy = 55 + (i % 2) * 14;
        return (
          <g key={i} transform={`translate(${cx}, ${cy})`}>
            <ellipse cx="0" cy="0" rx="9" ry="3.5" fill={c.accent} />
            <path d="M -8 -1 Q 0 -3 8 -1 L 5.5 3 Q 0 4 -5.5 3 Z" fill={c.accent} opacity="0.7" />
            <path d="M 0 -2 Q -2 -7 0 -10 Q 2 -7 0 -2 Z" fill="#fff0c0" />
          </g>
        );
      })}

      {([
        [40, 110, 4],
        [260, 105, 4],
        [45, 175, 3],
        [255, 170, 4],
      ] as const).map(([sx, sy, sr], i) => (
        <g key={i} transform={`translate(${sx}, ${sy})`}>
          <path
            d={`M 0 -${sr} L 0 ${sr} M -${sr} 0 L ${sr} 0 M -${sr * 0.7} -${sr * 0.7} L ${sr * 0.7} ${sr * 0.7} M -${sr * 0.7} ${sr * 0.7} L ${sr * 0.7} -${sr * 0.7}`}
            stroke={c.accent}
            strokeWidth="1"
            opacity="0.7"
          />
        </g>
      ))}

      <g transform="translate(150, 145)" opacity="0.5">
        <circle r="40" fill="none" stroke={c.accent} strokeWidth="1" opacity="0.5" />
        <circle r="32" fill="none" stroke={c.accent} strokeWidth="0.7" opacity="0.5" />
        {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
          <ellipse
            key={i}
            cx="0"
            cy="-28"
            rx="3"
            ry="7"
            fill={c.accent}
            opacity="0.5"
            transform={`rotate(${i * 45})`}
          />
        ))}
      </g>

      <Character x={150} y={140} model={model} scale={1.7} />
    </g>
  );
}

export function FriendsMotif({ c, model }: MotifProps) {
  const friends: Array<Pick<Model, "skin" | "hair" | "hairLength"> & { x: number }> = [
    { x: 60, skin: "#9a6037", hair: "#1a0f0a", hairLength: "short" },
    { x: 100, skin: "#f3d4b4", hair: "#c97a3a", hairLength: "long" },
    { x: 200, skin: "#e8b896", hair: "#2a1a10", hairLength: "short" },
    { x: 240, skin: "#6f4a2d", hair: "#15090a", hairLength: "long" },
  ];
  return (
    <g>
      <g opacity="0.5">
        <path d="M 20 130 L 20 80 L 50 60 L 80 80 L 80 130 Z" fill={c.accent} opacity="0.4" />
        <rect x="40" y="100" width="14" height="30" fill={c.bg} />
        <path d="M 220 130 L 220 75 L 250 55 L 280 75 L 280 130 Z" fill={c.accent} opacity="0.4" />
        <rect x="245" y="100" width="14" height="30" fill={c.bg} />
        <rect x="100" y="85" width="100" height="45" fill={c.accent} opacity="0.35" />
        <path d="M 100 85 L 150 60 L 200 85 Z" fill={c.accent} opacity="0.35" />
      </g>
      <line x1="0" y1="175" x2="300" y2="175" stroke={c.accent} strokeWidth="0.8" opacity="0.5" />
      {friends.map((f, i) => (
        <Character key={i} x={f.x} y={158} model={f} scale={1.0} />
      ))}
      <Character x={150} y={150} model={model} scale={1.4} />
    </g>
  );
}
